import { IconButton, Stack, Tooltip, Divider } from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import LinkIcon from "@mui/icons-material/Link";
import type { EditorSelection, ToolbarProps } from "./types";
import CreateLinkModal from "./CreateLinkModal";
import { useState } from "react";
import { useEditorState } from "@tiptap/react";

type LinkEditorState =
  | { status: "closed" }
  | { status: "open"; selection: EditorSelection; href: string };

const initialLinkEditorState: LinkEditorState = { status: "closed" };

function Toolbar({ editor, disabled }: ToolbarProps) {
  const [linkEditorState, setLinkEditorState] = useState<LinkEditorState>(
    initialLinkEditorState,
  );
  const editorState = useEditorState({
    editor,
    selector: ({ editor: currentEditor }) => ({
      boldIsActive: currentEditor?.isActive("bold") ?? false,
      italicIsActive: currentEditor?.isActive("italic") ?? false,
      bulletListIsActive: currentEditor?.isActive("bulletList") ?? false,
      orderedListIsActive: currentEditor?.isActive("orderedList") ?? false,
      linkIsActive: currentEditor?.isActive("link") ?? false,
      linkHref:
        (currentEditor?.getAttributes("link").href as string | undefined) ?? "",
      selectionIsEmpty: currentEditor?.state.selection.empty ?? true,
    }),
  });
  const canEdit = Boolean(editor) && !disabled;
  const canOpenLinkDialog =
    canEdit && (editorState?.linkIsActive || !editorState?.selectionIsEmpty);
  const activeFeatures = {
    bold: editorState?.boldIsActive,
    italic: editorState?.italicIsActive,
    bulletList: editorState?.bulletListIsActive,
    orderedList: editorState?.orderedListIsActive,
  };

  const formattingControls = [
    {
      feature: "bold" as const,
      label: "Bold",
      icon: <FormatBoldIcon fontSize="small" />,
      command: () => editor?.chain().focus().toggleBold().run(),
    },
    {
      feature: "italic" as const,
      label: "Italic",
      icon: <FormatItalicIcon fontSize="small" />,
      command: () => editor?.chain().focus().toggleItalic().run(),
    },
    {
      feature: "bulletList" as const,
      label: "Bullets",
      icon: <FormatListBulletedIcon fontSize="small" />,
      command: () => editor?.chain().focus().toggleBulletList().run(),
    },
    {
      feature: "orderedList" as const,
      label: "Numbered list",
      icon: <FormatListNumberedIcon fontSize="small" />,
      command: () => editor?.chain().focus().toggleOrderedList().run(),
    },
  ];

  const openLinkDialog = () => {
    if (!editor || !canOpenLinkDialog) return;

    setLinkEditorState({
      status: "open",
      selection: {
        from: editor.state.selection.from,
        to: editor.state.selection.to,
      },
      href: editorState?.linkIsActive ? editorState.linkHref : "",
    });
  };

  const restoreLinkSelection = () => {
    if (!editor || linkEditorState.status === "closed")
      return editor?.chain().focus();
    return editor.chain().focus().setTextSelection(linkEditorState.selection);
  };
  return (
    <>
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        role="toolbar"
        aria-label="Text formatting"
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          px: 1,
          py: 0.75,
          bgcolor: "action.hover",
        }}
      >
        {formattingControls.map(({ feature, label, icon, command }) => {
          const isActive = activeFeatures[feature];
          return (
            <Tooltip key={feature} title={label}>
              <span>
                <IconButton
                  size="small"
                  aria-label={label}
                  aria-pressed={isActive}
                  color={isActive ? "primary" : "default"}
                  disabled={!canEdit}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={command}
                >
                  {icon}
                </IconButton>
              </span>
            </Tooltip>
          );
        })}

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        <Tooltip title={editorState?.linkIsActive ? "Edit link" : "Add link"}>
          <span>
            <IconButton
              size="small"
              aria-label={editorState?.linkIsActive ? "Edit link" : "Add link"}
              aria-pressed={editorState?.linkIsActive}
              color={editorState?.linkIsActive ? "primary" : "default"}
              disabled={!canOpenLinkDialog}
              onMouseDown={(event) => event.preventDefault()}
              onClick={openLinkDialog}
            >
              <LinkIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <CreateLinkModal
        open={linkEditorState.status === "open"}
        initialHref={
          linkEditorState.status === "open" ? linkEditorState.href : ""
        }
        onCancel={() => setLinkEditorState(initialLinkEditorState)}
        onSave={(href) => {
          restoreLinkSelection()
            ?.extendMarkRange("link")
            .setLink({
              href,
              rel: "noopener",
              target: "_blank",
            })
            .run();
          setLinkEditorState(initialLinkEditorState);
        }}
        onRemove={() => {
          restoreLinkSelection()?.extendMarkRange("link").unsetLink().run();
          setLinkEditorState(initialLinkEditorState);
        }}
      />
    </>
  );
}

export default Toolbar;
